import apiService from './api';
import { TableColumn } from '../types';
import { TableDataResponse } from '../types/api.types';
import { TABLE_CONFIG } from '../utils/constants';

interface UpdateCellRequest {
  tableId: string;
  rowId: string;
  field: string;
  value: any;
  oldValue?: any;
}

interface BatchUpdateRequest {
  tableId: string;
  updates: Array<{
    rowId: string;
    field: string;
    value: any;
  }>;
}

// ─── 接口返回的列定义类型 ─────────────────────

interface ApiColumnChoice {
  readonly text: string;
  readonly value: string;
}

interface ApiColumnTranslation {
  readonly language: string;
  readonly translation: string;
}

interface ApiColumnMeta {
  readonly interface?: string;
  readonly special?: readonly string[];
  readonly translations?: readonly ApiColumnTranslation[];
  readonly options?: {
    readonly choices?: readonly ApiColumnChoice[];
    readonly multiple?: boolean;
  };
}

interface ApiColumn {
  readonly field: string;
  readonly type: string;
  readonly meta?: ApiColumnMeta | null;
}

// ─── 需要跳过的系统字段 ─────────────────────

const SYSTEM_FIELDS = new Set([
  'id', 'status', 'sort', 'user_created', 'date_created',
  'user_updated', 'date_updated',
]);

class TableService {
  // ========= 数据获取相关 =========

  /**
   * 获取表格数据 - 并行请求行数据 + 列配置
   *
   * @param tableId 表格ID（旧逐内联：作为 collection 名称；新逻辑：作为 filter[table_id] 的值）
   * @param collection Directus collection 名称，传入时使用 collection+tableId 模式，否则回退到旧的 tableId-as-collection 模式
   */
  async fetchTableData(tableId: string, collection?: string): Promise<TableDataResponse> {
    try {
      // 若有 collection，则使用 filter 模式；filter[table_id][_eq]=tableId & filter[is_active][_eq]=true
      // 否则保持旧行为：直接以 tableId 作为 collection 名称查询
      const targetCollection = collection ?? tableId;
      const filters = collection
        ? { 'filter[table_id][_eq]': tableId, 'filter[is_active][_eq]': 'true' }
        : undefined;

      const [dataResponse, columnsResponse] = await Promise.all([
        apiService.getTableData(targetCollection, filters),
        apiService.getTableColumns(targetCollection).catch((err) => {
          console.warn('获取列配置失败，将使用默认列:', err);
          return null;
        }),
      ]);

      const records = Array.isArray(dataResponse?.data) ? dataResponse.data : [];

      // ★ 如果列接口成功，动态转换；否则降级为默认列
      const columns = columnsResponse?.data
        ? this._transformApiColumns(columnsResponse.data, records)
        : this.generateDefaultColumns();

      return {
        columns,
        records,
        total: records.length,
      };
    } catch (error) {
      console.error('获取表格数据失败:', error);
      throw error;
    }
  }

  /**
   * 单独获取表格列配置
   * @param tableId 表格ID
   * @returns 转换后的列配置
   */
  async fetchTableColumns(tableId: string): Promise<TableColumn[]> {
    try {
      const response = await apiService.getTableColumns(tableId);
      return this._transformApiColumns(response?.data ?? [], []);
    } catch (error) {
      console.error('获取表格列配置失败:', error);
      return this.generateDefaultColumns();
    }
  }

  // ========= 数据更新相关 =========

  /**
   * 更新单个单元格数据
   * @param request 更新请求参数
   * @returns 更新结果
   */
  async updateCellData(request: UpdateCellRequest): Promise<any> {
    const { tableId, rowId, field, value, oldValue } = request;
    
    try {
      // 构建更新数据
      const updateData = { [field]: value };
      
      console.log('更新单元格数据:', {
        tableId,
        rowId,
        field,
        value,
        oldValue
      });

      // 调用API更新数据
      const response = await apiService.updateTableItem(tableId, rowId, updateData);
      
      console.log('单元格数据更新成功:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('更新单元格数据失败:', error);
      
      // 根据错误类型提供更友好的错误信息
      if (error.response?.status === 403) {
        throw new Error('没有编辑权限');
      } else if (error.response?.status === 404) {
        throw new Error('数据不存在');
      } else if (error.response?.status === 422) {
        throw new Error('数据格式不正确');
      }
      
      throw error;
    }
  }

  /**
   * 批量更新表格数据
   * @param request 批量更新请求参数
   * @returns 更新结果
   */
  async batchUpdateData(request: BatchUpdateRequest): Promise<any> {
    const { tableId, updates } = request;
    
    try {
      // 构建批量更新数据
      const batchData = updates.map(update => ({
        id: update.rowId,
        data: { [update.field]: update.value }
      }));

      console.log('批量更新表格数据:', { tableId, batchData });

      const response = await apiService.batchUpdateTableItems(tableId, batchData);
      
      console.log('批量更新成功:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('批量更新失败:', error);
      throw error;
    }
  }

  /**
   * 验证单元格数据
   * @param field 字段名
   * @param value 新值
   * @param column 列配置
   * @returns 验证结果
   */
  validateCellData(field: string, value: any, column: TableColumn): { isValid: boolean; error?: string } {
    try {
      // 根据字段类型进行特定验证
      switch (column.cellType) {
        case 'input':
          // 文本输入验证
          if (typeof value !== 'string' && value !== null) {
            return { isValid: false, error: '请输入有效的文本' };
          }
          break;

        case 'singleSelect':
          // 单选验证
          if (value && column.options && !column.options.includes(value)) {
            return { isValid: false, error: '请选择有效的选项' };
          }
          break;

        case 'multiSelect':
          // 多选验证
          if (value && Array.isArray(value) && column.options) {
            const invalidOptions = value.filter(v => !column.options!.includes(v));
            if (invalidOptions.length > 0) {
              return { isValid: false, error: `无效的选项: ${invalidOptions.join(', ')}` };
            }
          }
          break;

        default:
          break;
      }

      // 特定字段的业务验证
      if (field === 'percentage') {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0 || numValue > 100) {
          return { isValid: false, error: '百分比必须在0-100之间' };
        }
      }

      return { isValid: true };
      
    } catch (error) {
      console.error('验证数据时发生错误:', error);
      return { isValid: false, error: '数据验证失败' };
    }
  }

  /**
   * ★ 默认列配置（仅作为 fallback）
   */
  generateDefaultColumns(): TableColumn[] {
    return [
      {
        field: 'phase', //phase
        title: '阶段',
        width: 120,
        cellType: 'singleSelect',
        options: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'],
      },
      {
        field: 'tradeName', //tradeName
        title: '商品名称',
        width: 200,
        cellType: 'link', //button
      },
      {
        field: 'inci', //inci
        title: '国际药品注册',
        width: 150,
        cellType: 'text',
      },
      {
        field: 'function', //function
        title: '功能',
        width: 180,
        cellType: 'multiSelect',
        options: ['润肤剂', '乳化剂', '护肤剂', '活性成分', '防腐剂', '闭塞', '保湿剂', 'PH调节剂', '粘度调节剂', '抗氧化剂', '美学增强剂', '螯合剂', '助乳化剂',"成膜剂","稳定器", "着色剂", "香味"],
      },
      {
        field: 'supplier', //supplier
        title: '供应商',
        width: 150,
        cellType: 'text',
      },
      {
        field: 'estimated', //estimated
        title: '估计的 %',
        width: 120,
        cellType: 'text',
      },
      {
        field: 'percentage', //percentage
        title: '百分比 (%)',
        width: 120,
        cellType: 'input',
      },
    ];
  }

  generateEmptyData(rowCount: number = 15): any[] {
    return Array.from({ length: rowCount }, (_, index) => ({
      id: `row_${index + 1}`,
      phase: [
        {
          label: 'A', value: 'A',
        },
      ],
      tradeName: '⚡︎ 检测商品名称',
      inci: '燕麦甘油',
      function: [
        {
          label: '润肤剂', value: '润肤剂',
        },
        {
          label: '抗氧化剂', value: '抗氧化剂',
        }
      ],
      supplier: 'MakingCosmetics公司',
      estimated: '1-2%',
      percentage: '',
    }));
  }

  validateTableData(data: any[]): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!Array.isArray(data)) {
      errors.push('数据格式不正确');
      return { isValid: false, errors };
    }

    const totalPercentage = data.reduce((sum, row) => {
      const percentage = parseFloat(row.percentage) || 0;
      return sum + percentage;
    }, 0);

    if (Math.abs(totalPercentage - 100) > 0.1) {
      errors.push(`总百分比应为100%，当前为${totalPercentage.toFixed(1)}%`);
    }

    data.forEach((row, index) => {
      if (!row.ingredient || row.ingredient.trim() === '') {
        errors.push(`第${index + 1}行：成分名称不能为空`);
      }

      const percentage = parseFloat(row.percentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        errors.push(`第${index + 1}行：百分比必须在0-100之间`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // ========= 私有：列转换逻辑 =========

  /**
   * ★ 将接口返回的列数组转换为内部 TableColumn 格式
   * @param apiColumns 接口返回的原始列数组
   * @param records 行数据（用于推断单选/多选）
   */
  private _transformApiColumns(
    apiColumns: readonly ApiColumn[],
    records: readonly Record<string, any>[]
  ): TableColumn[] {
    if (!Array.isArray(apiColumns)) return this.generateDefaultColumns();

    const columns = apiColumns
      .filter((col) => col.field && !SYSTEM_FIELDS.has(col.field))
      .filter((col) => !col.field.startsWith('$'))  // 跳过 Directus 虚拟字段
      .map((col) => this._transformSingleColumn(col, records));

    return columns.length > 0 ? columns : this.generateDefaultColumns();
  }

  /**
   * 转换单个列
   */
  private _transformSingleColumn(
    apiCol: ApiColumn,
    records: readonly Record<string, any>[]
  ): TableColumn {
    const meta = apiCol.meta;
    const title = this._extractTitle(apiCol.field, meta);
    const cellType = this._resolveCellType(apiCol, records);
    const options = this._extractOptions(meta);

    const column: TableColumn = {
      field: apiCol.field,
      title,
      width: TABLE_CONFIG.DEFAULT_COLUMN_WIDTH,
      cellType,
    };

    if (options.length > 0) {
      column.options = options;
    }

    return column;
  }

  /**
   * 从 translations 中提取中文标题，fallback 为字段名
   */
  private _extractTitle(field: string, meta?: ApiColumnMeta | null): string {
    const translations = meta?.translations;
    if (!translations || translations.length === 0) return field;

    // 优先找 zh-CN
    const zhTranslation = translations.find((t) => t.language === 'zh-CN');
    if (zhTranslation?.translation) return zhTranslation.translation;

    // 其次用第一条
    return translations[0]?.translation || field;
  }

  /**
   * ★ 核心：根据 interface + type + 实际数据推断 cellType
   *
   * 映射规则：
   *  - interface=input, type=text       → 'input'
   *  - interface=input, type=float/int  → 'input'
   *  - interface=select-dropdown        → 看 multiple 或数据推断 → 'singleSelect' | 'multiSelect'
   *  - interface=input-rich-text        → 'text'
   *  - 其他                             → 'text'
   */
  private _resolveCellType(
    apiCol: ApiColumn,
    records: readonly Record<string, any>[]
  ): TableColumn['cellType'] {
    const meta = apiCol.meta;
    const interfaceType = meta?.interface ?? '';
    const dataType = apiCol.type ?? '';

    switch (interfaceType) {
      case 'input':
        return 'input';

      case 'select-dropdown': {
        // ★ 优先看显式配置
        if (meta?.options?.multiple === true) return 'multiSelect';
        if (meta?.options?.multiple === false) return 'singleSelect';

        // ★ 其次看实际数据推断
        if (this._isFieldArrayInRecords(apiCol.field, records)) {
          return 'multiSelect';
        }

        // ★ 最后看 type 字段：json 可能是多选，其他默认单选
        return dataType === 'json' ? 'multiSelect' : 'singleSelect';
      }

      case 'select-multiple-dropdown':
        return 'multiSelect';

      default:
        return 'text';
    }
  }

  /**
   * 检查某个字段在实际数据中是否存在数组值（用于推断多选）
   */
  private _isFieldArrayInRecords(
    field: string,
    records: readonly Record<string, any>[]
  ): boolean {
    // 取前 10 条数据采样，避免遍历大量数据
    const sampleSize = Math.min(records.length, 10);
    for (let i = 0; i < sampleSize; i++) {
      const value = records[i]?.[field];
      if (Array.isArray(value) && value.length > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * 从 meta.options.choices 中提取选项值
   */
  private _extractOptions(meta?: ApiColumnMeta | null): string[] {
    const choices = meta?.options?.choices;
    if (!Array.isArray(choices) || choices.length === 0) return [];
    return choices.map((choice) => choice.value ?? choice.text).filter(Boolean);
  }
}

export default new TableService();